import { collection, query, where, getDocs, getDocsFromServer, doc, getDoc, getDocFromServer, updateDoc, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../firebase';
import { YoklaAssignment, ScannedData, BlokMode, QuestionInfo, ExamMode, ExamModeDetectionResult, QuestionLock } from '../types';
import { getSubGroupForFenn } from '../constants/fennConfig';
import { normalizeXdil } from './adminService';
import { getPoolAssignmentsForTeacher } from './poolAssignmentService';
import { checkLockFromData } from './lockService';
import { matchesPrefixFilter } from '../utils/studentIdPrefixUtils';
import { collectQuestionFields, pickStudentField } from '../utils/studentFieldUtils';

// SubGroup'un boş olup olmadığını kontrol et (undefined, null, boş string veya "-" tire)
const isSubGroupEmpty = (subGroup: string | undefined | null): boolean => {
  return !subGroup || subGroup === '-' || subGroup.trim() === '';
};

// Grade normalize fonksiyonu (number veya string olabilir, string'e çevir)
const normalizeGrade = (grade: string | number | null | undefined): string => {
  if (grade === null || grade === undefined) return '';
  // Number ise string'e çevir
  if (typeof grade === 'number') {
    return String(grade);
  }
  // String ise olduğu gibi dön
  return String(grade);
};

/**
 * Detects exam mode (Blok vs Buraxılısh) by analyzing student document structure.
 * Independent of exam configuration, used for validation.
 *
 * Detection algorithm:
 * 1. Check for Buraxılısh-specific fields: variant, xdil, buildingName
 * 2. Check for Blok-specific fields: specialtyGroup, subGroup
 * 3. Use question count heuristic (9 for Blok, 8-24 for Buraxılısh)
 * 4. Calculate confidence based on number of indicators
 * 5. Compare with config mode and generate warnings if mismatch
 *
 * @param student - The student data to analyze
 * @param configMode - Optional configured exam mode for validation
 * @returns ExamModeDetectionResult with detected mode, confidence, and indicators
 *
 * @example
 * ```typescript
 * const student = { variant: 'B', xdil: 'İngilis dili' };
 * const result = detectExamMode(student, 'blok');
 * // Returns: { mode: 'buraxilis', confidence: 'high', indicators: [...], warnings: [...] }
 * ```
 */
export const detectExamMode = (
  student: ScannedData,
  configMode?: ExamMode
): ExamModeDetectionResult => {
  const result: ExamModeDetectionResult = {
    mode: 'blok',
    confidence: 'low',
    indicators: []
  };

  // Check for Buraxılısh-specific fields
  const hasVariant = student.variant && student.variant !== '-';
  const hasXdil = student.xdil && student.xdil !== '-';
  const hasBuildingName = student.buildingName;

  // Check for Blok-specific fields
  const hasSpecialtyGroup = student.specialtyGroup && student.specialtyGroup !== '-';
  const hasSubGroup = student.subGroup && student.subGroup !== '-';

  // Count Q## fields to estimate question count
  const questionFields = Object.keys(student).filter(k => /^Q\d+$/.test(k));
  const questionCount = questionFields.length;

  // Detection logic
  const buraxilisIndicators: string[] = [];
  const blokIndicators: string[] = [];

  if (hasVariant) buraxilisIndicators.push('Has variant field');
  if (hasXdil) buraxilisIndicators.push('Has xdil field');
  if (hasBuildingName) buraxilisIndicators.push('Has buildingName field');

  if (hasSpecialtyGroup) blokIndicators.push('Has specialtyGroup field');
  if (hasSubGroup) blokIndicators.push('Has subGroup field');

  // Question count heuristic
  if (questionCount > 15) {
    buraxilisIndicators.push(`Question count ${questionCount} suggests Buraxılısh`);
  } else if (questionCount <= 9) {
    blokIndicators.push(`Question count ${questionCount} suggests Blok`);
  }

  // Determine mode based on indicators
  if (buraxilisIndicators.length >= 2) {
    result.mode = 'buraxilis';
    result.confidence = 'high';
    result.indicators = buraxilisIndicators;
  } else if (blokIndicators.length >= 2) {
    result.mode = 'blok';
    result.confidence = 'high';
    result.indicators = blokIndicators;
  } else if (buraxilisIndicators.length > blokIndicators.length) {
    result.mode = 'buraxilis';
    result.confidence = 'medium';
    result.indicators = buraxilisIndicators;
  } else if (blokIndicators.length > buraxilisIndicators.length) {
    result.mode = 'blok';
    result.confidence = 'medium';
    result.indicators = blokIndicators;
  } else {
    // Low confidence - use config as fallback
    result.mode = configMode || 'blok';
    result.confidence = 'low';
    result.indicators = ['Insufficient indicators, using config'];
  }

  // Check for config mismatch
  if (configMode && configMode !== result.mode) {
    result.warnings = [
      `Config says '${configMode}' but detection says '${result.mode}'`,
      `Confidence: ${result.confidence}`,
      `Indicators: ${result.indicators.join(', ')}`
    ];
  }

  return result;
};

export const getTeacherAssignments = async (teacherId: string): Promise<YoklaAssignment[]> => {
  try {
    console.log('🔍 yoklaService: Öğretmen ID ile atamalar aranıyor:', teacherId);

    // 1. Get individual assignments (original logic)
    // Filter out apellyasiya type documents - they should only appear in appeals tab
    const q = query(
      collection(db, 'yoxla-s'),
      where('hocaId', 'in', [teacherId, 'Jb871YwbUOf53dK1yDSg96kTfya2'])
    );

    console.log('📡 yoklaService: Firestore query çalıştırılıyor...');
    const querySnapshot = await getDocs(q);

    console.log('📊 yoklaService: Query sonucu - document sayısı:', querySnapshot.docs.length);

    const assignments: YoklaAssignment[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();

      // Skip apellyasiya type documents - they should only appear in appeals tab
      if (data.type === 'apellyasiya') {
        console.log('⏭️ yoklaService: Apellyasiya document skipped:', doc.id);
        return;
      }

      // Skip pool assignments - they have hocaId: 'pool' (will be fetched separately)
      if (data.assignmentType === 'pool') {
        console.log('⏭️ yoklaService: Pool assignment skipped (will fetch separately):', doc.id);
        return;
      }

      // Eski 'blok' field'ını 'qrup' olarak map et (backward compatibility)
      const assignment: YoklaAssignment = {
        id: doc.id,
        hocaId: data.hocaId,
        hocaName: data.hocaName,
        fenn: data.fenn,
        sinif: data.sinif,
        qrup: data.qrup || data.blok, // Eski 'blok' değerini kullan
        variant: data.variant, // Buraxılış variant (A, B, C, D)
        subGroup: data.subGroup,
        status: data.status,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        completedCount: data.completedCount,
        totalCount: data.totalCount,
        collectionName: data.collectionName || 'scanned2610',
        examType: data.examType || 'Blok',
        questionNumber: data.questionNumber,
        questionNumbers: data.questionNumbers, // Buraxılış sual array
        buildingName: data.buildingName,
        assignmentType: data.assignmentType || 'individual', // Default to individual
        studentIdPrefixFilter: data.studentIdPrefixFilter
      };

      assignments.push(assignment);
    });

    // 2. Get pool assignments for this teacher
    try {
      const poolAssignments = await getPoolAssignmentsForTeacher(teacherId);
      assignments.push(...poolAssignments);
      console.log(`🏊 yoklaService: ${poolAssignments.length} pool assignment(s) added`);
    } catch (poolError) {
      console.warn('⚠️ yoklaService: Error fetching pool assignments:', poolError);
      // Continue without pool assignments - don't fail the whole request
    }

    console.log('✅ yoklaService: Toplam atama sayısı:', assignments.length);
    return assignments;
  } catch (error) {
    console.error('❌ yoklaService: Yoklama atamaları alınırken hata:', error);
    throw error;
  }
};

export type GetScannedDataOptions = {
  /** Refresh sonrası köhnə cache qarşısını almaq üçün birbaşa serverdən oxu */
  fromServer?: boolean;
};

export const getScannedDataByAssignment = async (
  assignment: YoklaAssignment,
  teacherId: string,
  options?: GetScannedDataOptions
): Promise<ScannedData[]> => {
  const fromServer = options?.fromServer === true;
  try {
    const collectionName = assignment.collectionName || 'scanned2610';
    const examType = assignment.examType || 'Blok';
    
    console.log('🔍 yoklaService: Ogrenci verileri araniyor - Atama:', assignment, fromServer ? '(server)' : '');
    console.log('🔍 yoklaService: ExamType:', examType, 'Collection:', collectionName);
    console.log('🔍 yoklaService: Filtreler - Sinif:', assignment.sinif, 'Qrup:', assignment.qrup, 'SubGroup:', assignment.subGroup);
    
    // For buraxılış, filter by grade, variant, xdil and optionally buildingName
    if (examType === 'buraxilis') {
      const normalizedSinif = normalizeGrade(assignment.sinif);
      const variant = assignment.variant || assignment.qrup; // variant field or fallback to qrup

      console.log('🔍 yoklaService: Buraxılış filtreler - Sinif:', normalizedSinif, 'Variant:', variant);

      const getQuery = fromServer ? getDocsFromServer : getDocs;
      // grade sahəsi: string və number ilə sorğu (Firestore-da hər iki tip ola bilər)
      const queryPromises: Promise<any>[] = [];

      const gradeStringQuery = query(
        collection(db, collectionName),
        where('grade', '==', normalizedSinif),
        where('variant', '==', variant)
      );
      queryPromises.push(getQuery(gradeStringQuery));

      if (!isNaN(Number(assignment.sinif))) {
        const numericSinif = Number(assignment.sinif);
        const gradeNumberQuery = query(
          collection(db, collectionName),
          where('grade', '==', numericSinif),
          where('variant', '==', variant)
        );
        queryPromises.push(getQuery(gradeNumberQuery));
      }

      const buraxilisSnapshots = await Promise.all(queryPromises);

      // Merge results and remove duplicates
      const seenDocIds = new Set<string>();
      let buraxilisDocs: any[] = [];

      buraxilisSnapshots.forEach((snapshot: any) => {
        snapshot.forEach((doc: any) => {
          if (!seenDocIds.has(doc.id)) {
            seenDocIds.add(doc.id);
            buraxilisDocs.push(doc);
          }
        });
      });

      // Scanner bəzən grade/variant əvəzinə Grade/sinif/Variant yazır.
      // İndeksli sorğu boşdursa bütün koleksiyonu oxuyub sahə adını çevik tutuşdur.
      if (buraxilisDocs.length === 0) {
        console.log('⚠️ yoklaService: grade+variant sorğusu boşdur, sahə adı fərqi yoxlanılır:', collectionName);
        const allSnap = await getQuery(query(collection(db, collectionName)));
        allSnap.forEach((docSnap: any) => {
          const data = docSnap.data();
          const gradeValue = String(pickStudentField(data, 'grade', 'sinif', 'class') ?? '').trim();
          const variantValue = String(pickStudentField(data, 'variant') ?? '').trim();
          const gradeMatches = gradeValue === normalizedSinif || gradeValue === String(assignment.sinif);
          const variantMatches = variantValue.toUpperCase() === String(variant).toUpperCase();
          if (gradeMatches && variantMatches && !seenDocIds.has(docSnap.id)) {
            seenDocIds.add(docSnap.id);
            buraxilisDocs.push(docSnap);
          }
        });
        console.log('📊 yoklaService: Çevik oxumadan sonra şagird:', buraxilisDocs.length);
      }

      // Apply buildingName filter if specified (client-side filtering)
      if (assignment.buildingName && assignment.buildingName !== 'Hamısı') {
        buraxilisDocs = buraxilisDocs.filter(doc => {
          const data = doc.data();
          return data.buildingName === assignment.buildingName;
        });
      }

      // Apply studentIdPrefixFilter if present on assignment
      if (assignment.studentIdPrefixFilter && assignment.studentIdPrefixFilter.enabled && assignment.studentIdPrefixFilter.prefixes.length > 0) {
        buraxilisDocs = buraxilisDocs.filter((doc: any) =>
          matchesPrefixFilter(
            pickStudentField(doc.data(), 'student_id', 'studentId', 'sagird_id'),
            assignment.studentIdPrefixFilter
          )
        );
        console.log('🔍 yoklaService: Prefix filter applied:', assignment.studentIdPrefixFilter.prefixes.join(', '), '-> Students:', buraxilisDocs.length);
      }

      console.log('📊 yoklaService: Buraxılış - Sinif', normalizedSinif, 'Variant', variant, '- Toplam öğrenci:', buraxilisDocs.length);

      const foreignLanguageSubjects = ['İngilis dili', 'Fransız dili', 'Alman dili', 'Rus dili'];
      const assignmentXdilNorm = normalizeXdil(assignment.fenn);
      const isForeignLanguage = foreignLanguageSubjects.includes(assignmentXdilNorm);

      let students: ScannedData[] = [];
      buraxilisDocs.forEach((doc) => {
        const data = doc.data();
        const xdil = pickStudentField(data, 'xdil', 'xarici_dil', 'foreignLanguage') || data.xdil;

        // Xarici dil fənlərində yalnız uyğun xdil olan şagirdləri daxil et (assignment.fenn "Fransız" və ya "Fransız dili" ola bilər)
        if (isForeignLanguage) {
          const studentXdil = normalizeXdil(xdil);
          if (studentXdil !== assignmentXdilNorm) {
            return;
          }
        }

        students.push({
          id: doc.id, // Firestore document ID
          ...data,
          ...collectQuestionFields(data),
          student_id: String(pickStudentField(data, 'student_id', 'studentId', 'sagird_id') || doc.id),
          grade: pickStudentField(data, 'grade', 'sinif', 'class') ?? data.grade,
          variant: pickStudentField(data, 'variant') ?? data.variant,
          xdil,
          firstName: pickStudentField(data, 'firstName', 'first_name', 'ad', 'name') ?? data.firstName,
          lastName: pickStudentField(data, 'lastName', 'last_name', 'soyad', 'surname') ?? data.lastName
        } as ScannedData);
      });

      // Buraxılış üçün: Admin "Boş sual" işarələri və digər müəllimlərin verdiyi ballar
      // bu müəllimin tayin siyahısına düşməməlidir.
      // questionNumber (tekil) VEYA questionNumbers (çoğul) array kontrol et
      const questionNumbersToCheck = assignment.questionNumbers && assignment.questionNumbers.length > 0
        ? assignment.questionNumbers
        : assignment.questionNumber
          ? [assignment.questionNumber]
          : [];

      if (questionNumbersToCheck.length > 0) {
        // Her öğrenci için: En az bir soru bu teacher için açık olmalı
        // (yani hiç yoklanmamış VEYA bu teacher tarafından yoklanmış)
        students = students.filter(student => {
          // Bu öğrenci için en az bir soru bu teacher'a açık mı?
          const hasOpenQuestion = questionNumbersToCheck.some((qNum: string) => {
            const qField = `${qNum}_c`;
            const qValue: any = (student as any)[qField];

            if (!qValue) {
              // Heç kim qiymətləndirməyib – bu müəllim üçün açıqdır
              return true;
            }

            if (qValue && typeof qValue === 'object') {
              const isAdminEmpty =
                qValue.hocaId === 'admin' &&
                qValue.note &&
                qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');

              if (isAdminEmpty) {
                // Admin boş kağıt – bu müəllim heç görməməlidir
                return false;
              }

              // Yalnız bu müəllimin verdiyi ballar üçün siyahıda saxla
              return qValue.hocaId === teacherId;
            }

            return true;
          });

          return hasOpenQuestion;
        });

        console.log('📊 yoklaService: Buraxılış - Filtrelenmiş öğrenci sayısı (teacherId ilə):', students.length);
      }

      // Pool assignment additional filtering: exclude students with active locks from other teachers
      if (assignment.assignmentType === 'pool' && questionNumbersToCheck.length > 0) {
        const now = Date.now();

        students = students.filter(student => {
          // For pool assignments, check each question for locks from other teachers
          for (const questionNum of questionNumbersToCheck) {
            const lockResult = checkLockFromData(student, questionNum, teacherId);

            if (lockResult.isLocked) {
              // This question is locked by another teacher - exclude from this teacher's list
              console.log(`🔒 yoklaService: Student ${student.student_id} excluded - ${questionNum} locked by ${lockResult.lock?.teacherName}`);
              return false;
            }
          }

          // No active locks from other teachers - include student
          return true;
        });

        console.log('📊 yoklaService: Pool - After lock filtering:', students.length, 'students');
      }
      
      // Document ID'ye göre sırala (sisteme eklenme sırasına göre - önemli: telefon yoklaması için)
      // Bu sayede yeni eklenen "İngilis" xdil'li öğrenciler sıralamayı bozmaz
      students.sort((a, b) => {
        const idA = a.id || a.student_id || '';
        const idB = b.id || b.student_id || '';
        return idA.localeCompare(idB);
      });
      
      return students;
    }
    
    // Blok exam: Original logic (fromServer ilə cache bypass)
    const getQueryBlok = fromServer ? getDocsFromServer : getDocs;
    console.log('📡 yoklaService: Tum scanned collection kontrol ediliyor...');
    const allDocsQuery = query(collection(db, collectionName));
    const allDocsSnapshot = await getQueryBlok(allDocsQuery);
    console.log('📊 yoklaService: Tum scanned documents:', allDocsSnapshot.docs.length);
    
    // İlk birkaç document'i gösterelim
    allDocsSnapshot.docs.slice(0, 3).forEach((doc, index) => {
      const data = doc.data();
      console.log(`📄 yoklaService: Document ${index + 1}:`, doc.id);
      console.log(`📄 yoklaService: Document ${index + 1} data:`, data);
      console.log(`📄 yoklaService: Document ${index + 1} grade:`, data.grade, 'tipi:', typeof data.grade);
      console.log(`📄 yoklaService: Document ${index + 1} specialtyGroup:`, data.specialtyGroup, 'tipi:', typeof data.specialtyGroup);
      console.log(`📄 yoklaService: Document ${index + 1} subGroup:`, data.subGroup, 'tipi:', typeof data.subGroup);
    });
    
    // Grade'i normalize et (string'e çevir)
    const normalizedSinif = normalizeGrade(assignment.sinif);
    
    // Hem string hem number olarak sorgula (Firestore'da her iki tip de olabilir!)
    const queryPromises: Promise<any>[] = [];
    
    // String olarak sorgula
    const stringQuery = query(
      collection(db, collectionName),
      where('grade', '==', normalizedSinif),
      where('specialtyGroup', '==', assignment.qrup)
    );
    queryPromises.push(getQueryBlok(stringQuery));
    
    // Number olarak da sorgula (eğer number'a çevrilebiliyorsa)
    if (!isNaN(Number(assignment.sinif))) {
      const numericSinif = Number(assignment.sinif);
      const numberQuery = query(
        collection(db, collectionName),
        where('grade', '==', numericSinif),
        where('specialtyGroup', '==', assignment.qrup)
      );
      queryPromises.push(getQueryBlok(numberQuery));
    }
    
    console.log('📡 yoklaService: Filtrelenmis query calistiriliyor...');
    console.log('📡 yoklaService: Query detaylari - grade (string):', normalizedSinif, 'specialtyGroup:', assignment.qrup);
    
    // Her iki sorguyu paralel çalıştır ve sonuçları birleştir
    const querySnapshots = await Promise.all(queryPromises);
    
    // Tüm sonuçları birleştir (duplicate'leri çıkar - document ID'ye göre)
    const uniqueDocs = new Map<string, QueryDocumentSnapshot<DocumentData>>();
    querySnapshots.forEach(snapshot => {
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        if (!uniqueDocs.has(doc.id)) {
          uniqueDocs.set(doc.id, doc);
        }
      });
    });
    
    // Map'ten array'e çevir
    const querySnapshot = {
      docs: Array.from(uniqueDocs.values())
    } as any;
    
    console.log('📊 yoklaService: Filtrelenmis query sonucu - document sayisi:', querySnapshot.docs.length);
    
    // Fenn'e göre beklenen subGroup'u belirle
    const expectedSubGroup = getSubGroupForFenn(assignment.fenn, assignment.qrup);
    console.log('🔍 yoklaService: Fenn:', assignment.fenn, 'Qrup:', assignment.qrup, 'Expected SubGroup:', expectedSubGroup);
    
    // SubGroup normalize fonksiyonu (RI -> Rİ, ri -> Rİ vb.)
    const normalizeSubGroup = (sg: string | null | undefined): string | null => {
      if (!sg) return null;
      return sg.toUpperCase().replace('I', 'İ'); // Normal I'yı Türkçe İ'ye çevir
    };
    
    // Öğrencileri filtrele - sadece yoklanmamış ve kendi yokladığı öğrenciler
    const filteredStudents: ScannedData[] = [];
    
    for (const doc of querySnapshot.docs) {
      const data = doc.data();
      
      // SubGroup kontrolü - Fenn'e göre
      if (expectedSubGroup) {
        // Bu fenn bir subGroup'a aitse, sadece o subGroup'lu öğrencileri al
        const normalizedExpected = normalizeSubGroup(expectedSubGroup);
        const normalizedStudent = normalizeSubGroup(data.subGroup);
        
        if (normalizedStudent !== normalizedExpected) {
          console.log('⏭️ yoklaService: SubGroup uyuşmuyor - Atlanıyor:', data.student_id, 'Expected:', expectedSubGroup, 'Found:', data.subGroup);
          continue;
        }
      }
      // expectedSubGroup undefined ise (Azərbaycan dili gibi), TÜM öğrencileri al (subGroup kontrolü yapma)
      
      // Spread ALL Firestore data including Q## fields (Q28, Q29, etc.)
      const studentData: ScannedData = {
        id: doc.id, // Firestore document ID
        ...data,  // Include ALL fields from Firestore (including Q28, Q29, etc.)
        student_id: data.student_id || doc.id
      } as ScannedData;
      
      // Bu öğrenci için bu fenn'de yoklama var mı kontrol et
      const fennData = data[assignment.fenn];
      
      if (!fennData) {
        // Yoklama yapılmamış - ekle
        console.log('✅ yoklaService: Yoklanmamış öğrenci:', studentData.student_id);
        filteredStudents.push(studentData);
      } else if (fennData.hocaId === teacherId) {
        // Bu öğretmen tarafından yoklanmış - ekle
        console.log('✅ yoklaService: Bu öğretmen tarafından yoklanmış:', studentData.student_id);
        filteredStudents.push(studentData);
      } else {
        // Başka öğretmen tarafından yoklanmış - ekleme
        console.log('❌ yoklaService: Başka öğretmen tarafından yoklanmış:', studentData.student_id, 'HocaId:', fennData.hocaId);
      }
    }
    
    // Document ID'ye göre sırala (sisteme eklenme sırasına göre - önemli: telefon yoklaması için)
    filteredStudents.sort((a, b) => {
      const idA = a.id || a.student_id || '';
      const idB = b.id || b.student_id || '';
      return idA.localeCompare(idB);
    });
    
    console.log('📊 yoklaService: Filtrelenmiş öğrenci sayısı:', filteredStudents.length);
    return filteredStudents;
  } catch (error) {
    console.error('❌ yoklaService: Taranmis veriler alinirken hata:', error);
    throw error;
  }
};

export const updateAssignmentStatus = async (assignmentId: string, status: string): Promise<void> => {
  try {
    console.log('📝 yoklaService: Atama statusu güncelleniyor:', assignmentId, status);
    const docRef = doc(db, 'yoxla-s', assignmentId);
    await updateDoc(docRef, { status });
    console.log('✅ yoklaService: Atama statusu başarıyla güncellendi');
  } catch (error) {
    console.error('❌ yoklaService: Atama statusu güncellenirken hata:', error);
    throw error;
  }
};

export const getStudentGradingData = async (studentId: string, fenn: string): Promise<{ [questionNumber: string]: string } | null> => {
  try {
    console.log('🔍 yoklaService: Öğrenci değerlendirme verileri aranıyor:', studentId, fenn);
    
    // studentId'yi hem string hem number olarak dene
    const studentIdStr = String(studentId);
    const studentIdNum = !isNaN(Number(studentId)) ? Number(studentId) : null;
    
    // 1. Önce string document ID ile dene
    let docRef = doc(db, 'scanned2610', studentIdStr);
    let docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data[fenn] && typeof data[fenn] === 'object') {
        const gradingData = data[fenn];
        console.log('📊 yoklaService: Değerlendirme verileri bulundu (string ID):', gradingData);
        
        const scores: { [questionNumber: string]: string } = {};
        Object.keys(gradingData).forEach(key => {
          if (key !== 'hocaId' && key !== 'hoca') {
            scores[key] = gradingData[key];
          }
        });
        
        return scores;
      }
    }
    
    // 2. Number ID ile de dene (eğer number ise)
    if (studentIdNum !== null) {
      docRef = doc(db, 'scanned2610', String(studentIdNum));
      docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data[fenn] && typeof data[fenn] === 'object') {
          const gradingData = data[fenn];
          console.log('📊 yoklaService: Değerlendirme verileri bulundu (number ID):', gradingData);
          
          const scores: { [questionNumber: string]: string } = {};
          Object.keys(gradingData).forEach(key => {
            if (key !== 'hocaId' && key !== 'hoca') {
              scores[key] = gradingData[key];
            }
          });
          
          return scores;
        }
      }
    }
    
    // 3. Direct ID ile bulunamadıysa, student_id field'ı ile ara (hem string hem number)
    console.log('🔍 yoklaService: Direct ID ile bulunamadı, student_id field ile aranıyor...');
    const queryPromises: Promise<any>[] = [];
    
    // String student_id ile ara
    queryPromises.push(getDocs(query(collection(db, 'scanned2610'), where('student_id', '==', studentIdStr))));
    
    // Number student_id ile de ara (eğer number ise)
    if (studentIdNum !== null) {
      queryPromises.push(getDocs(query(collection(db, 'scanned2610'), where('student_id', '==', studentIdNum))));
    }
    
    const querySnapshots = await Promise.all(queryPromises);
    
    // İlk bulunan sonucu kullan
    for (const querySnapshot of querySnapshots) {
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        console.log('📊 yoklaService: Student ID ile document bulundu:', doc.id, data);
        
        if (data[fenn] && typeof data[fenn] === 'object') {
          const gradingData = data[fenn];
          console.log('📊 yoklaService: Değerlendirme verileri bulundu (student_id field ile):', gradingData);
          
          const scores: { [questionNumber: string]: string } = {};
          Object.keys(gradingData).forEach(key => {
            if (key !== 'hocaId' && key !== 'hoca') {
              scores[key] = gradingData[key];
            }
          });
          
          return scores;
        }
      }
    }
    
    console.log('⚠️ yoklaService: Değerlendirme verisi bulunamadı');
    return null;
  } catch (error) {
    console.error('❌ yoklaService: Öğrenci değerlendirme verileri alınırken hata:', error);
    throw error;
  }
};

export const saveGradingResults = async (
  studentId: string, 
  teacherId: string, 
  teacherName: string,
  fenn: string, 
  scores: { [questionNumber: string]: string }
): Promise<void> => {
  try {
    console.log('💾 yoklaService: Değerlendirme sonuçları kaydediliyor...');
    console.log('💾 yoklaService: Student ID:', studentId, 'Fenn:', fenn, 'Scores:', scores);
    
    // studentId'yi hem string hem number olarak dene
    const studentIdStr = String(studentId);
    const studentIdNum = !isNaN(Number(studentId)) ? Number(studentId) : null;
    
    // 1. Önce string document ID ile dene
    let docRef = doc(db, 'scanned2610', studentIdStr);
    let docSnap = await getDoc(docRef);
    let foundDoc: any = null;
    
    if (docSnap.exists()) {
      foundDoc = { id: docSnap.id, data: () => docSnap.data() };
      console.log('💾 yoklaService: Document bulundu (string ID):', foundDoc.id);
    } else if (studentIdNum !== null) {
      // 2. Number ID ile de dene (eğer number ise)
      docRef = doc(db, 'scanned2610', String(studentIdNum));
      docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        foundDoc = { id: docSnap.id, data: () => docSnap.data() };
        console.log('💾 yoklaService: Document bulundu (number ID):', foundDoc.id);
      }
    }
    
    if (!foundDoc) {
      // 3. student_id field ile ara (hem string hem number)
      console.log('🔍 yoklaService: Document ID ile bulunamadı, student_id field ile aranıyor...');
      const queryPromises: Promise<any>[] = [];
      
      // String student_id ile ara
      queryPromises.push(getDocs(query(collection(db, 'scanned2610'), where('student_id', '==', studentIdStr))));
      
      // Number student_id ile de ara (eğer number ise)
      if (studentIdNum !== null) {
        queryPromises.push(getDocs(query(collection(db, 'scanned2610'), where('student_id', '==', studentIdNum))));
      }
      
      const querySnapshots = await Promise.all(queryPromises);
      
      // İlk bulunan sonucu kullan
      for (const querySnapshot of querySnapshots) {
        if (!querySnapshot.empty) {
          foundDoc = querySnapshot.docs[0];
          console.log('🎯 yoklaService: Student ID ile document bulundu:', foundDoc.id);
          break;
        }
      }
    }
    
    if (foundDoc) {
      const currentData = foundDoc.data();
      console.log('💾 yoklaService: Mevcut veri:', currentData);
      
      // Doğru document referansını kullan
      const correctDocRef = doc(db, 'scanned2610', foundDoc.id);
      
      const updatedData = {
        ...currentData,
        [fenn]: {
          ...scores,
          hocaId: teacherId,
          hoca: teacherName
        }
      };
      
      console.log('💾 yoklaService: Güncellenecek veri:', updatedData);
      
      await updateDoc(correctDocRef, updatedData);
      console.log('✅ yoklaService: Değerlendirme sonuçları başarıyla kaydedildi');
    } else {
      console.error('❌ yoklaService: Document bulunamadı - Student ID:', studentIdStr, studentIdNum);
      throw new Error(`Öğrenci verisi bulunamadı - Student ID: ${studentIdStr}`);
    }
  } catch (error) {
    console.error('❌ yoklaService: Değerlendirme sonuçları kaydedilirken hata:', error);
    throw error;
  }
};

// ============================================================================
// Blok Dual-Mode Utility Functions
// ============================================================================

// Set to track students that have already logged warnings (avoid spam)
const warnedStudents = new Set<string>();

/**
 * Detects whether a Blok exam student uses legacy or new format.
 * Legacy format has individual Q## fields (Q28, Q29, etc.)
 * New format has a `questions` array with file paths
 *
 * Results are cached on the student object to avoid repeated detection.
 *
 * @param student - The student data to analyze
 * @returns 'new' if questions array exists, 'legacy' if Q## fields exist, 'legacy' as fallback
 */
export const detectBlokMode = (student: ScannedData): BlokMode => {
  // Check cache first (stored on student object via blokMode property)
  if ((student as any).blokMode) {
    return (student as any).blokMode;
  }

  // Check for new format: questions array
  if (student.questions && Array.isArray(student.questions) && student.questions.length > 0) {
    (student as any).blokMode = 'new';
    return 'new';
  }

  // Check for legacy format: individual Q## fields
  // Need to check if student has any Q## keys (case-sensitive, like Q28, Q29, etc.)
  const hasQuestionFields = Object.keys(student).some(key => /^Q\d+$/.test(key));

  if (hasQuestionFields) {
    (student as any).blokMode = 'legacy';
    return 'legacy';
  }

  // Additional check: look for Q## with .path properties in nested objects
  // Some Firestore data structures might have these as nested objects
  for (const key of Object.keys(student)) {
    const value = (student as any)[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Check if this has a 'path' property (characteristic of Q## question fields)
      if ('path' in value) {
        // Also check for typical Q## metadata fields to confirm
        const hasBluePixels = Object.keys(value).some(k => k.startsWith('blue_'));
        const hasThreshold = 'threshold_used' in value || 'threshold_level' in value;
        const hasIsEmpty = 'is_empty' in value;

        if (hasBluePixels || hasThreshold || hasIsEmpty) {
          // This looks like a Q## field with question metadata
          (student as any).blokMode = 'legacy';
          console.log(`✅ yoklaService: Detected legacy format for student ${student.student_id} via field ${key}`);
          return 'legacy';
        }
      }
    }
  }

  // Fallback - assume legacy for backward compatibility (warn only once)
  if (!warnedStudents.has(student.student_id)) {
    console.warn(`⚠️ yoklaService: Could not detect Blok mode for student ${student.student_id}, defaulting to 'legacy'`);
    warnedStudents.add(student.student_id);
  }
  (student as any).blokMode = 'legacy';
  return 'legacy';
};

/**
 * Gets the actual Blok questions for a student with their checked status.
 * Works for both new format (questions array) and legacy format (Q## fields).
 * Filters out admin empty papers (is_empty or double_check_empty).
 *
 * @param student - The student data
 * @returns Array of question objects with qNum and checked_c status (excluding admin empty papers)
 */
export const getActualBlokQuestions = (student: ScannedData): Array<{ qNum: number; checked_c?: boolean }> => {
  const mode = detectBlokMode(student);

  if (mode === 'new') {
    // New format: questions array
    const studentQuestions = (student as any).questions || [];

    // Filter out admin empty papers (check Q## field for is_empty or double_check_empty)
    return studentQuestions
      .filter((q: any) => {
        const qNum = q.questionNumber || '';
        const qData = (student as any)[qNum]; // Check Q## field (not Q##_c)

        // Include question if NOT marked as empty by admin
        if (!qData || typeof qData !== 'object') {
          return true; // No data, include it
        }

        // Exclude if admin marked as empty
        const isAdminEmpty = qData.is_empty === true || qData.double_check_empty === true;
        return !isAdminEmpty;
      })
      .map((q: any) => ({
        qNum: parseInt(q.questionNumber?.replace('Q', '') || '0'),
        checked_c: q.checked_c
      }));
  } else {
    // Legacy format: Only the 9 questions used in Blok system
    // Questions Q1-Q27, Q31-Q57, Q61-Q87 DO NOT EXIST in this Blok system
    const legacyQuestions = [
      'Q28', 'Q29', 'Q30',  // Fənn 1
      'Q58', 'Q59', 'Q60',  // Fənn 2
      'Q88', 'Q89', 'Q90'   // Fənn 3
    ];

    return legacyQuestions
      .filter(qNum => {
        // Check Q## field for admin empty paper markers
        const qData = (student as any)[qNum];

        // Include question if NOT marked as empty by admin
        if (!qData || typeof qData !== 'object') {
          return true; // No data, include it
        }

        // Exclude if admin marked as empty
        const isAdminEmpty = qData.is_empty === true || qData.double_check_empty === true;
        return !isAdminEmpty;
      })
      .map(qNum => {
        const checkedField = `${qNum}_c` as keyof typeof student;
        const checkedData = student[checkedField];
        return {
          qNum: parseInt(qNum.replace('Q', '')),
          checked_c: checkedData ? true : undefined
        };
      });
  }
};

/**
 * Gets the image path for a specific question, regardless of data format.
 * Works for both legacy (Q##.path) and new (questions array) formats.
 *
 * @param student - The student data
 * @param questionNumber - Question number (e.g., "Q28", "Q88")
 * @returns The image path or null if not found
 */
export const getQuestionPath = (student: ScannedData, questionNumber: string): string | null => {
  const mode = detectBlokMode(student);

  if (mode === 'new') {
    // New format: search in questions array
    if (!student.questions || student.questions.length === 0) {
      return null;
    }

    // Case-insensitive search for question number in filename
    const searchPattern = new RegExp(`${questionNumber}`, 'i');
    const found = student.questions.find(path => searchPattern.test(path));

    return found || null;
  } else {
    // Legacy format: get from Q## field
    const questionData = (student as any)[questionNumber];
    const path = questionData?.path || null;

    // Debug logging for image path issues
    if (!path && !questionData) {
      console.log(`🔍 getQuestionPath: Question field ${questionNumber} not found on student ${student.student_id}`);
      console.log(`🔍 Available keys:`, Object.keys(student).filter(k => k.startsWith('Q')).slice(0, 10));
    } else if (!path) {
      console.log(`🔍 getQuestionPath: Question field ${questionNumber} exists but has no path:`, questionData);
    } else {
      console.log(`✅ getQuestionPath: Found path for ${questionNumber}:`, path.substring(0, 50) + '...');
    }

    return path;
  }
};

/**
 * Gets the empty status for a specific question.
 * For legacy format: returns Q##.is_empty
 * For new format: returns true if question is NOT in questions array
 *
 * @param student - The student data
 * @param questionNumber - Question number (e.g., "Q28", "Q88")
 * @returns boolean for empty status, null if unknown
 */
export const getQuestionEmptyStatus = (student: ScannedData, questionNumber: string): boolean | null => {
  const mode = detectBlokMode(student);

  if (mode === 'new') {
    // For new format, check if question exists in questions array
    // Missing from array = effectively empty
    if (!student.questions || student.questions.length === 0) {
      return null;
    }

    const searchPattern = new RegExp(`${questionNumber}`, 'i');
    const exists = student.questions.some(path => searchPattern.test(path));

    // If question doesn't exist in array, it's effectively empty
    return !exists;
  } else {
    // Legacy format: get is_empty from Q## field
    const questionData = (student as any)[questionNumber];
    return questionData?.is_empty ?? null;
  }
};

/**
 * Gets all question data for a student assignment.
 * Returns a unified structure regardless of data format.
 *
 * @param student - The student data
 * @param assignment - The assignment with questionNumbers array
 * @returns Array of QuestionInfo objects
 */
export const getQuestionsForStudent = (
  student: ScannedData,
  assignment: YoklaAssignment
): QuestionInfo[] => {
  const questions: QuestionInfo[] = [];
  const questionNumbers = assignment.questionNumbers || [];

  for (const qNum of questionNumbers) {
    const path = getQuestionPath(student, qNum);
    const isEmpty = getQuestionEmptyStatus(student, qNum);

    questions.push({
      questionNumber: qNum,
      imagePath: path,
      isEmpty
    });
  }

  return questions;
};
